import React, { useRef, useEffect, useState } from 'react';
import { Hands } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import { drawLandmarks } from '@mediapipe/drawing_utils';
import './App.css';

// Frame-busting protection
if (window.self !== window.top) {
  const allowedDomains = ['discoverelearning.com', 'reactdev01.elearningnebula.com', '360.articulate.com', 'articulateusercontent.com'];
  const parentDomain = document.referrer ? new URL(document.referrer).hostname : '';

  if (!allowedDomains.some(domain => parentDomain.includes(domain))) {
    window.top!.location = window.self.location;
  }
}

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
}

const Confetti: React.FC<{ show: boolean }> = ({ show }) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const animationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (show) {
      // Create confetti pieces
      const newPieces: ConfettiPiece[] = [];
      const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98FB98'];

      for (let i = 0; i < 100; i++) {
        newPieces.push({
          id: i,
          x: Math.random() * window.innerWidth,
          y: -10,
          vx: (Math.random() - 0.5) * 4,
          vy: Math.random() * 3 + 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 4,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 6
        });
      }
      setPieces(newPieces);

      // Animate confetti
      const animate = () => {
        setPieces(prev => prev.map(piece => ({
          ...piece,
          x: piece.x + piece.vx,
          y: piece.y + piece.vy,
          rotation: piece.rotation + piece.rotationSpeed,
          vy: piece.vy + 0.1 // gravity
        })).filter(piece => piece.y < window.innerHeight + 20));

        animationRef.current = requestAnimationFrame(animate);
      };

      animate();
    } else {
      setPieces([]);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [show]);

  if (!show || pieces.length === 0) return null;

  return (
    <div className="confetti-container">
      {pieces.map(piece => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: piece.x,
            top: piece.y,
            backgroundColor: piece.color,
            width: piece.size,
            height: piece.size,
            transform: `rotate(${piece.rotation}deg)`
          }}
        />
      ))}
    </div>
  );
};

interface HandLandmark {
  x: number;
  y: number;
  z: number;
}

interface HandResults {
  multiHandLandmarks?: HandLandmark[][];
  multiHandedness?: { label: string; score: number }[];
}

const isMobileDevice = () => {
  // Check if device is mobile based on user agent and screen dimensions
  const userAgent = navigator.userAgent || navigator.vendor;
  const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  // Use screen dimensions instead of window dimensions for iframe compatibility
  const isSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;

  return isMobileUserAgent || (isTouchDevice && isSmallScreen);
};

const checkDomainAccess = () => {
  // Allowed domains for iframe embedding
  const allowedDomains = [
    'https://discoverelearning.com',
    'https://360.articulate.com',
    'https://articulateusercontent.com'
  ];

  try {
    // Check if running in an iframe
    const isInIframe = window !== window.top;

    if (!isInIframe) {
      // Allow direct access (not in iframe)
      return true;
    }

    // Check parent frame origin
    let parentOrigin = '';
    try {
      parentOrigin = window.top?.location.origin || '';
    } catch (e) {
      // Cross-origin, check document.referrer as fallback
      const referrer = document.referrer;
      if (referrer) {
        const referrerUrl = new URL(referrer);
        parentOrigin = referrerUrl.origin;
      }
    }

    // Check if parent origin is in allowed domains
    const isAllowed = allowedDomains.some(domain => {
      const allowedUrl = new URL(domain);
      return parentOrigin === allowedUrl.origin;
    });

    return isAllowed;
  } catch (error) {
    console.error('Domain access check failed:', error);
    // Default to false for security
    return false;
  }
};

interface AppProps {
  preselectedLetter?: 'A' | 'B' | 'C';
}

function App({ preselectedLetter }: AppProps = {}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPermissionGranted, setIsPermissionGranted] = useState<boolean | null>(null);
  const [isMobile] = useState<boolean>(isMobileDevice());
  const [isDomainAllowed] = useState<boolean>(checkDomainAccess());
  const [selectedLetter, setSelectedLetter] = useState<string | null>(preselectedLetter || null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionResult, setDetectionResult] = useState<{
    isCorrect: boolean;
    confidence: number;
    letterDetected?: string;
  } | null>(null);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [detectionStage, setDetectionStage] = useState<'waiting' | 'show_first_hand' | 'show_second_hand' | 'make_letter' | 'success'>('show_first_hand');
  const [showConfetti, setShowConfetti] = useState(false);
  const [successDetected, setSuccessDetected] = useState(false);
  const [confettiFired, setConfettiFired] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const confettiFiredRef = useRef(false);
  const messageAnimationRef = useRef(0);
  const handsRef = useRef<Hands | null>(null);
  const cameraRef = useRef<Camera | null>(null);
  const animationRef = useRef<number | null>(null);
  const successTimeoutRef = useRef<number | null>(null);

  const startVideoDrawing = () => {
    const drawFrame = () => {
      // Stop drawing if confetti has fired (success achieved)
      if (confettiFiredRef.current) {
        return;
      }

      const canvas = canvasRef.current;
      const video = videoRef.current;

      if (canvas && video && video.readyState >= 2) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          try {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Add a simple indicator that video is active
            ctx.strokeStyle = '#00FF00';
            ctx.lineWidth = 2;
            ctx.strokeRect(10, 10, 100, 30);
            ctx.fillStyle = '#00FF00';
            ctx.font = '14px Arial';
            ctx.fillText('Camera Active', 15, 30);

          } catch (error) {
            console.error('Error drawing video frame:', error);
          }
        }
      }
      animationRef.current = requestAnimationFrame(drawFrame);
    };
    drawFrame();
  };

  const resetApplication = () => {
    console.log('🔄 Resetting application...');
    // Reset all states
    setDetectionStage('show_first_hand');
    setShowConfetti(false);
    setSuccessDetected(false);
    setConfettiFired(false);
    setShowResetButton(false);
    setDetectionResult(null);
    confettiFiredRef.current = false;
    messageAnimationRef.current = 0;

    // Clear timeouts
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }

    // Cancel any running animation frames
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Resume video if it was paused
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }

    // Restart video drawing
    startVideoDrawing();

    // Clear the hands reference (already closed on success)
    handsRef.current = null;

    // Wait a moment for cleanup, then reinitialize
    setTimeout(() => {
      console.log('🔄 Reinitializing hand detection...');
      initializeHandDetection();
    }, 100);
  };

  const requestCameraPermission = async () => {
    try {
      console.log('🎥 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: 640,
          height: 480,
          facingMode: 'user'
        }
      });
      console.log('✅ Camera access granted');

      // Store the stream and update state
      setCameraStream(stream);
      setIsPermissionGranted(true);
      setIsDetecting(true);
    } catch (error) {
      console.error('❌ Camera permission denied or error:', error);
      setIsPermissionGranted(false);
    }
  };

  const isHandOpen = (hand: HandLandmark[]) => {
    // Check if fingertips are extended relative to their base joints
    const fingerTips = [8, 12, 16, 20]; // Index, middle, ring, pinky tips
    const fingerBases = [6, 10, 14, 18]; // Finger base joints

    let extendedFingers = 0;
    let fingersPointingUp = 0;

    fingerTips.forEach((tipIndex, i) => {
      const tip = hand[tipIndex];
      const base = hand[fingerBases[i]];

      // Check if fingertip is further from wrist than base (extended)
      const wrist = hand[0];
      const tipDistance = Math.sqrt(Math.pow(tip.x - wrist.x, 2) + Math.pow(tip.y - wrist.y, 2));
      const baseDistance = Math.sqrt(Math.pow(base.x - wrist.x, 2) + Math.pow(base.y - wrist.y, 2));

      if (tipDistance > baseDistance * 1.1) {
        extendedFingers++;

        // Check if finger is pointing upwards (tip Y position is above base Y position)
        // In canvas coordinates, Y increases downward, so tip.y < base.y means pointing up
        if (tip.y < base.y - 0.02) { // Small threshold to account for slight variations
          fingersPointingUp++;
        }
      }
    });

    // Hand is open if at least 3 fingers are extended AND at least 2 are pointing upward
    const isOpen = extendedFingers >= 3;
    const isPointingUp = fingersPointingUp >= 2;

    console.log('🖐️ Hand orientation check:', {
      extendedFingers,
      fingersPointingUp,
      isOpen,
      isPointingUp,
      result: isOpen && isPointingUp
    });

    return isOpen && isPointingUp;
  };

  const detectOpenHands = (results: HandResults) => {
    if (!results.multiHandLandmarks || results.multiHandLandmarks.length < 2) {
      return false;
    }

    // Check if both hands have fingers extended (open hands)
    const areHandsOpen = results.multiHandLandmarks.every(hand => isHandOpen(hand));
    return areHandsOpen;
  };

  const detectBSLLetterA = (results: HandResults) => {
    // BSL Letter A: One hand open (thumb hand), other hand's index finger touching the thumb hand's thumb
    // Stricter rules: 1) One hand must be open with fingers pointing up, 2) Other hand's index touches the open hand's thumb

    if (!results.multiHandLandmarks || results.multiHandLandmarks.length < 2) {
      console.log('🔍 BSL Detection: Not enough hands detected:', results.multiHandLandmarks?.length || 0);
      return { isLetterA: false, confidence: 0 };
    }

    const hand1 = results.multiHandLandmarks[0];
    const hand2 = results.multiHandLandmarks[1];

    // Check which hand is open (thumb hand)
    const hand1IsOpen = isHandOpen(hand1);
    const hand2IsOpen = isHandOpen(hand2);

    console.log('🔍 Hand openness:', { hand1IsOpen, hand2IsOpen });

    // We need exactly one open hand (thumb hand) and one non-open hand
    let thumbHand, otherHand;

    if (hand1IsOpen && !hand2IsOpen) {
      thumbHand = hand1;
      otherHand = hand2;
      console.log('🔍 Hand1 is thumb hand, Hand2 is other hand');
    } else if (hand2IsOpen && !hand1IsOpen) {
      thumbHand = hand2;
      otherHand = hand1;
      console.log('🔍 Hand2 is thumb hand, Hand1 is other hand');
    } else {
      console.log('🔍 BSL Detection: Need exactly one open hand and one closed hand');
      return { isLetterA: false, confidence: 0 };
    }

    // Hand landmarks: 4 = thumb tip, 8 = index finger tip
    const thumbHandThumbTip = thumbHand[4];
    const otherHandIndexTip = otherHand[8];

    // Calculate distance between other hand's index finger and thumb hand's thumb
    const distance = Math.sqrt(
      Math.pow(otherHandIndexTip.x - thumbHandThumbTip.x, 2) +
      Math.pow(otherHandIndexTip.y - thumbHandThumbTip.y, 2)
    );

    // Threshold for "touching" (normalized coordinates)
    const touchThreshold = 0.05;
    const isTouching = distance < touchThreshold;

    // Calculate confidence based on how close the touching points are
    const confidence = isTouching ? Math.round((1 - (distance / touchThreshold)) * 100) : 0;

    console.log('🔍 BSL Detection (Strict):', {
      distance: distance.toFixed(3),
      threshold: touchThreshold,
      isTouching,
      isLetterA: isTouching,
      confidence
    });

    return {
      isLetterA: isTouching,
      confidence: Math.max(0, Math.min(100, confidence))
    };
  };

  const detectBSLLetterB = (results: HandResults) => {
    // BSL Letter B: Both hands make circles (all fingertips touch thumb) and the circles touch each other (spectacles gesture)

    if (!results.multiHandLandmarks || results.multiHandLandmarks.length < 2) {
      console.log('🔍 BSL Letter B: Not enough hands detected:', results.multiHandLandmarks?.length || 0);
      return { isLetterB: false, confidence: 0 };
    }

    const hand1 = results.multiHandLandmarks[0];
    const hand2 = results.multiHandLandmarks[1];

    // Hand landmarks: 4 = thumb tip, 8 = index tip, 12 = middle tip, 16 = ring tip, 20 = pinky tip
    const hand1ThumbTip = hand1[4];
    const hand1Fingertips = [hand1[8], hand1[12], hand1[16], hand1[20]]; // index, middle, ring, pinky
    const hand2ThumbTip = hand2[4];
    const hand2Fingertips = [hand2[8], hand2[12], hand2[16], hand2[20]]; // index, middle, ring, pinky

    // Helper function to check if all fingertips are close to thumb (making a circle)
    const checkCircleFormation = (thumbTip: any, fingertips: any[]) => {
      const fingerThreshold = 0.06; // Slightly more lenient since we need all fingers
      const fingersNearThumb = fingertips.map(fingertip => {
        const distance = Math.sqrt(
          Math.pow(fingertip.x - thumbTip.x, 2) +
          Math.pow(fingertip.y - thumbTip.y, 2)
        );
        return { distance, isNear: distance < fingerThreshold };
      });

      // Require at least 3 out of 4 fingertips to be near thumb (more flexible but still strict)
      const nearCount = fingersNearThumb.filter(f => f.isNear).length;
      const isCircle = nearCount >= 3;

      // Calculate average distance for confidence calculation
      const avgDistance = fingersNearThumb.reduce((sum, f) => sum + f.distance, 0) / fingersNearThumb.length;

      return { isCircle, avgDistance, nearCount, fingersNearThumb };
    };

    // Check circle formation for both hands
    const hand1Circle = checkCircleFormation(hand1ThumbTip, hand1Fingertips);
    const hand2Circle = checkCircleFormation(hand2ThumbTip, hand2Fingertips);

    // Calculate circle centers (average of thumb and all fingertips)
    const hand1CircleCenter = {
      x: (hand1ThumbTip.x + hand1Fingertips.reduce((sum, f) => sum + f.x, 0) / hand1Fingertips.length) / 2,
      y: (hand1ThumbTip.y + hand1Fingertips.reduce((sum, f) => sum + f.y, 0) / hand1Fingertips.length) / 2
    };
    const hand2CircleCenter = {
      x: (hand2ThumbTip.x + hand2Fingertips.reduce((sum, f) => sum + f.x, 0) / hand2Fingertips.length) / 2,
      y: (hand2ThumbTip.y + hand2Fingertips.reduce((sum, f) => sum + f.y, 0) / hand2Fingertips.length) / 2
    };

    // Check if the two circles are connected (hands are close together like spectacles)
    const circleConnectionDistance = Math.sqrt(
      Math.pow(hand1CircleCenter.x - hand2CircleCenter.x, 2) +
      Math.pow(hand1CircleCenter.y - hand2CircleCenter.y, 2)
    );

    // Threshold for circles being connected (spectacles position) - must actually touch
    const connectionThreshold = 0.04; // Much stricter - circles must really be touching
    const circlesConnected = circleConnectionDistance < connectionThreshold;

    // Additional check: hands should be roughly at the same height (horizontal alignment like spectacles)
    const heightDifference = Math.abs(hand1CircleCenter.y - hand2CircleCenter.y);
    const heightThreshold = 0.06; // Stricter horizontal alignment requirement
    const handsAligned = heightDifference < heightThreshold;

    // BOTH hands must make circles AND be connected for Letter B
    const isLetterB = hand1Circle.isCircle && hand2Circle.isCircle && circlesConnected && handsAligned;

    // Calculate confidence based on how well formed the circles are and how well connected
    let confidence = 0;
    if (hand1Circle.isCircle && hand2Circle.isCircle && circlesConnected && handsAligned) {
      // Circle quality based on how close fingers are to thumb (lower distance = higher quality)
      const circle1Quality = Math.max(0, 1 - (hand1Circle.avgDistance / 0.06));
      const circle2Quality = Math.max(0, 1 - (hand2Circle.avgDistance / 0.06));
      const connectionQuality = Math.max(0, 1 - (circleConnectionDistance / connectionThreshold));
      const alignmentQuality = Math.max(0, 1 - (heightDifference / heightThreshold));

      // Bonus for having more fingers touching thumb
      const fingerBonus1 = hand1Circle.nearCount / 4;
      const fingerBonus2 = hand2Circle.nearCount / 4;

      confidence = Math.round(((circle1Quality + circle2Quality + connectionQuality + alignmentQuality + fingerBonus1 + fingerBonus2) / 6) * 100);
    } else {
      // If not all conditions are met, confidence should be 0
      confidence = 0;
    }

    console.log('🔍 BSL Letter B Detection:', {
      hand1CircleAvgDistance: hand1Circle.avgDistance.toFixed(3),
      hand1FingersNearThumb: hand1Circle.nearCount,
      hand1MakesCircle: hand1Circle.isCircle,
      hand2CircleAvgDistance: hand2Circle.avgDistance.toFixed(3),
      hand2FingersNearThumb: hand2Circle.nearCount,
      hand2MakesCircle: hand2Circle.isCircle,
      circleConnectionDistance: circleConnectionDistance.toFixed(3),
      circlesConnected,
      handsAligned,
      heightDifference: heightDifference.toFixed(3),
      isLetterB,
      confidence
    });

    return {
      isLetterB,
      confidence: Math.max(0, Math.min(100, confidence))
    };
  };

  const detectBSLLetterC = (results: HandResults) => {
    // BSL Letter C: Detection based on MediaPipe landmarks for proper C arch shape
    // Key requirement: 6,7,8 (index) should be above 2,3,4 (thumb) with 3-7 forming the arc

    if (!results.multiHandLandmarks || results.multiHandLandmarks.length < 1) {
      console.log('🔍 BSL Letter C: No hands detected');
      return { isLetterC: false, confidence: 0 };
    }

    // Use the first detected hand (C only requires one hand)
    const hand = results.multiHandLandmarks[0];

    // MediaPipe landmarks for thumb and index finger
    const thumb2 = hand[2];   // Thumb MCP joint
    const thumb3 = hand[3];   // Thumb IP joint
    const thumb4 = hand[4];   // Thumb tip
    const index6 = hand[6];   // Index DIP joint
    const index7 = hand[7];   // Index PIP joint
    const index8 = hand[8];   // Index tip

    // REQUIREMENT 1: Index landmarks (6,7,8) should be "above" thumb landmarks (2,3,4)
    // This means they should have different Y coordinates showing the arch structure
    const thumbCenterY = (thumb2.y + thumb3.y + thumb4.y) / 3;
    const indexCenterY = (index6.y + index7.y + index8.y) / 3;

    // For a proper C, index should be offset from thumb (either above or below depending on orientation)
    const verticalSeparation = Math.abs(thumbCenterY - indexCenterY);
    const minVerticalSeparation = 0.04; // Minimum offset to show distinct arch structure
    const hasVerticalSeparation = verticalSeparation >= minVerticalSeparation;

    // REQUIREMENT 2: Distance between landmark 3 and 7 should be the greatest (forms the arc)
    const distance_3_7 = Math.sqrt(
      Math.pow(thumb3.x - index7.x, 2) +
      Math.pow(thumb3.y - index7.y, 2)
    );

    // Compare with other key distances to ensure 3-7 is the arc peak
    const distance_2_6 = Math.sqrt(
      Math.pow(thumb2.x - index6.x, 2) +
      Math.pow(thumb2.y - index6.y, 2)
    );
    const distance_4_8 = Math.sqrt(
      Math.pow(thumb4.x - index8.x, 2) +
      Math.pow(thumb4.y - index8.y, 2)
    );

    // 3-7 should be the longest distance (arc peak)
    const is_3_7_longest = distance_3_7 > distance_2_6 && distance_3_7 > distance_4_8;

    // REQUIREMENT 3: Finger length validation - thumb and index should be extended
    const thumbLength = Math.sqrt(
      Math.pow(thumb4.x - thumb2.x, 2) +
      Math.pow(thumb4.y - thumb2.y, 2)
    );
    const indexLength = Math.sqrt(
      Math.pow(index8.x - index6.x, 2) +
      Math.pow(index8.y - index6.y, 2)
    );

    const minThumbLength = 0.06;  // Minimum thumb extension
    const minIndexLength = 0.07;  // Minimum index extension - adjusted based on real data
    const thumbExtended = thumbLength >= minThumbLength;
    const indexExtended = indexLength >= minIndexLength;

    // REQUIREMENT 4: Proper C orientation - thumb and index should form horizontal arch
    // Check if the overall shape is oriented horizontally (left-right) rather than vertically
    const thumbSpanX = Math.abs(thumb4.x - thumb2.x);
    const indexSpanX = Math.abs(index8.x - index6.x);
    const avgHorizontalSpan = (thumbSpanX + indexSpanX) / 2;

    const thumbSpanY = Math.abs(thumb4.y - thumb2.y);
    const indexSpanY = Math.abs(index8.y - index6.y);
    const avgVerticalSpan = (thumbSpanY + indexSpanY) / 2;

    // For proper C, horizontal span should be greater than vertical span
    const isHorizontalOrientation = avgHorizontalSpan > avgVerticalSpan * 0.8;

    // REQUIREMENT 5: Arc validation - 3-7 distance should be reasonable for C shape
    const minArcDistance = 0.08;  // Minimum distance for proper C arc
    const maxArcDistance = 0.35;  // Maximum distance - adjusted based on real data (was 0.25)
    const hasValidArcDistance = distance_3_7 >= minArcDistance && distance_3_7 <= maxArcDistance;

    // ALL REQUIREMENTS MUST BE MET for Letter C
    const isLetterC = hasVerticalSeparation && is_3_7_longest && thumbExtended &&
                      indexExtended && isHorizontalOrientation && hasValidArcDistance;

    // Calculate confidence based on how well each requirement is met
    let confidence = 0;
    if (isLetterC) {
      const separationScore = Math.min(1, verticalSeparation / 0.08);
      const arcDominanceScore = is_3_7_longest ? 1 : 0;
      const thumbScore = Math.min(1, thumbLength / 0.08);
      const indexScore = Math.min(1, indexLength / 0.10);
      const orientationScore = isHorizontalOrientation ? 1 : 0;
      const arcDistanceScore = Math.max(0, 1 - Math.abs(distance_3_7 - 0.20) / 0.15);

      confidence = Math.round(((separationScore + arcDominanceScore + thumbScore + indexScore + orientationScore + arcDistanceScore) / 6) * 100);
    }

    console.log('🔍 BSL Letter C MediaPipe Detection:', {
      verticalSeparation: verticalSeparation.toFixed(3),
      hasVerticalSeparation,
      distance_3_7: distance_3_7.toFixed(3),
      distance_2_6: distance_2_6.toFixed(3),
      distance_4_8: distance_4_8.toFixed(3),
      is_3_7_longest,
      thumbLength: thumbLength.toFixed(3),
      thumbExtended,
      indexLength: indexLength.toFixed(3),
      indexExtended,
      avgHorizontalSpan: avgHorizontalSpan.toFixed(3),
      avgVerticalSpan: avgVerticalSpan.toFixed(3),
      isHorizontalOrientation,
      hasValidArcDistance,
      isLetterC,
      confidence
    });

    return {
      isLetterC,
      confidence: Math.max(0, Math.min(100, confidence))
    };
  };

  // Generic BSL letter detection function
  const detectBSLLetter = (results: HandResults, letter: string) => {
    switch (letter) {
      case 'A':
        const resultA = detectBSLLetterA(results);
        return {
          isCorrect: resultA.isLetterA,
          confidence: resultA.confidence,
          letterDetected: resultA.isLetterA ? 'A' : undefined
        };
      case 'B':
        const resultB = detectBSLLetterB(results);
        return {
          isCorrect: resultB.isLetterB,
          confidence: resultB.confidence,
          letterDetected: resultB.isLetterB ? 'B' : undefined
        };
      case 'C':
        const resultC = detectBSLLetterC(results);
        return {
          isCorrect: resultC.isLetterC,
          confidence: resultC.confidence,
          letterDetected: resultC.isLetterC ? 'C' : undefined
        };
      default:
        return { isCorrect: false, confidence: 0 };
    }
  };

  const onResults = (results: HandResults) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (!canvas || !video || confettiFiredRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas and draw video frame
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch (error) {
      return;
    }

    console.log('🔄 onResults called, current stage:', detectionStage, 'hands detected:', results.multiHandLandmarks?.length || 0);

    // Stage-based detection logic
    const handsCount = results.multiHandLandmarks?.length || 0;

    // Determine what stage we should be in based on hand count
    if (detectionStage === 'show_first_hand' && handsCount >= 2) {
      console.log('✅ Both hands detected! Skipping to make_letter');
      setDetectionStage('make_letter');
    } else if (detectionStage === 'show_first_hand' && handsCount >= 1) {
      // For letter C, skip to make_letter since it only needs one hand
      if (selectedLetter === 'C') {
        console.log('✅ One hand detected for letter C! Moving to make_letter');
        setDetectionStage('make_letter');
      } else {
        console.log('✅ First hand detected! Moving to show_second_hand');
        setDetectionStage('show_second_hand');
      }
    } else if (detectionStage === 'show_second_hand' && handsCount >= 2) {
      console.log('✅ Both hands detected! Moving to make_letter');
      setDetectionStage('make_letter');
    }

    // Handle BSL letter detection when we have the required number of hands
    const requiredHands = selectedLetter === 'C' ? 1 : 2;
    if (handsCount >= requiredHands && selectedLetter) {
      const detection = detectBSLLetter(results, selectedLetter);
      setDetectionResult(detection);

      console.log('🔍 BSL Check:', {
        letter: selectedLetter,
        isCorrect: detection.isCorrect,
        confidence: detection.confidence,
        confettiFired,
        confettiFiredRef: confettiFiredRef.current
      });

      if (detection.isCorrect && detection.confidence > 20 && !confettiFiredRef.current) {
        // Success! Pause video and show celebration (only once)
        setDetectionStage('success');
        setSuccessDetected(true);
        setShowConfetti(true);
        setConfettiFired(true);
        confettiFiredRef.current = true;

        // Stop video drawing and pause the video
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }

        // Pause the actual video element
        if (videoRef.current) {
          videoRef.current.pause();
        }

        // Stop MediaPipe hand detection to prevent further processing
        if (handsRef.current) {
          handsRef.current.close();
        }

        // Hide confetti after 5 seconds and show reset button
        successTimeoutRef.current = window.setTimeout(() => {
          setShowConfetti(false);
          setShowResetButton(true);
        }, 5000);

        return; // Don't draw anything more, freeze the frame
      }
    }

    // Draw all detected hands
    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      results.multiHandLandmarks.forEach((landmarks, index) => {
        let handColor = '#FF0000'; // Default red

        // Always color hands: first hand = red, second hand = blue
        handColor = index === 0 ? '#FF0000' : '#0000FF';

        drawLandmarks(ctx, landmarks, {
          color: handColor,
          lineWidth: 1,
          radius: 3
        });

        // Highlight thumb and index finger tips during letter detection stage
        if (detectionStage === 'make_letter') {
          const thumbTip = landmarks[4];
          const indexTip = landmarks[8];

          // Draw larger, more visible circles for thumb and index
          ctx.fillStyle = handColor;
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;

          // Thumb tip
          ctx.beginPath();
          ctx.arc(thumbTip.x * canvas.width, thumbTip.y * canvas.height, 12, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();

          // Index finger tip
          ctx.beginPath();
          ctx.arc(indexTip.x * canvas.width, indexTip.y * canvas.height, 12, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
        }
      });


      // Draw modern styled message overlay (only if not successful)
      if (!confettiFiredRef.current) {
        let message = '';
        let isSuccess = false;

        if (handsCount === 0) {
          message = 'Raise one hand to the camera';
        } else if (handsCount === 1) {
          // For letter C, go straight to making the letter
          if (selectedLetter === 'C') {
            message = `Perfect! Now make BSL Letter ${selectedLetter}`;
            isSuccess = true;
          } else {
            message = 'Great! Now raise your other hand';
            isSuccess = true;
          }
        } else if (handsCount >= 2 && detectionStage !== 'make_letter') {
          message = selectedLetter ? `Perfect! Now make BSL Letter ${selectedLetter}` : 'Perfect! Now make the BSL letter';
          isSuccess = true;
        } else if (detectionStage === 'make_letter') {
          const requiredHandsForMessage = selectedLetter === 'C' ? 1 : 2;
          if (results.multiHandLandmarks.length < requiredHandsForMessage) {
            message = selectedLetter === 'C' ?
              `Show one hand for Letter ${selectedLetter}` :
              selectedLetter ? `Show both hands for Letter ${selectedLetter}` : 'Show both hands for the letter';
          } else if (selectedLetter) {
            const detection = detectBSLLetter(results, selectedLetter);
            if (detection.isCorrect) {
              message = `BSL Letter ${selectedLetter}: ${detection.confidence}%`;
              isSuccess = true;
            } else {
              // Letter-specific instructions
              if (selectedLetter === 'A') {
                message = 'Keep one hand open, touch its thumb with other index finger';
              } else if (selectedLetter === 'B') {
                message = 'Make circles with both hands and connect them together';
              } else {
                message = `Make the BSL Letter ${selectedLetter} sign`;
              }
            }
          }
        }

        if (message) {
          // Animate slide-in from left
          messageAnimationRef.current = Math.min(messageAnimationRef.current + 0.08, 1);
          const animationProgress = messageAnimationRef.current;
          const easeOut = 1 - Math.pow(1 - animationProgress, 3); // Cubic ease-out

          // Create modern message box sliding in from left
          const boxPaddingX = 25;
          const boxPaddingY = 15;
          const boxHeight = 60;
          const boxRadius = 12;

          // Measure text to determine box width with larger font
          ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          const textMetrics = ctx.measureText(message);
          const textWidth = textMetrics.width;
          const boxWidth = textWidth + (boxPaddingX * 2);

          // Calculate slide-in position
          const finalX = 20;
          const startX = -boxWidth - 20;
          const boxX = startX + (finalX - startX) * easeOut;
          const boxY = 20;

          // Draw modern white box with shadow
          ctx.save();

          // Shadow
          ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
          ctx.fillRect(boxX + 3, boxY + 3, boxWidth, boxHeight);

          // Main box
          ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
          ctx.beginPath();
          ctx.roundRect(boxX, boxY, boxWidth, boxHeight, boxRadius);
          ctx.fill();

          // Border
          ctx.strokeStyle = isSuccess ? '#4CAF50' : '#ff6b35';
          ctx.lineWidth = 3;
          ctx.stroke();

          // Text
          ctx.fillStyle = '#2c2c2c';
          ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(message, boxX + boxPaddingX, boxY + boxHeight / 2);

          ctx.restore();
        }
      }
    } else if (!confettiFiredRef.current) {
      // No hands detected - show stage-specific instructions with modern styling
      let message = '';

      if (detectionStage === 'show_first_hand') {
        message = 'Raise one hand to the camera';
      } else if (detectionStage === 'show_second_hand') {
        message = 'Raise your second hand';
      } else if (detectionStage === 'make_letter') {
        if (selectedLetter === 'A') {
          message = 'Keep one hand open, touch its thumb with other index finger';
        } else if (selectedLetter === 'B') {
          message = 'Make circles with both hands and connect them together';
        } else {
          message = selectedLetter ? `Make the BSL Letter ${selectedLetter} sign` : 'Make the BSL letter sign';
        }
      } else {
        message = 'Show your hands to the camera';
      }

      if (message) {
        // Animate slide-in from left
        messageAnimationRef.current = Math.min(messageAnimationRef.current + 0.08, 1);
        const animationProgress = messageAnimationRef.current;
        const easeOut = 1 - Math.pow(1 - animationProgress, 3); // Cubic ease-out

        // Create modern message box sliding in from left
        const boxPaddingX = 25;
        const boxPaddingY = 15;
        const boxHeight = 60;
        const boxRadius = 12;

        // Measure text to determine box width with larger font
        ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        const textMetrics = ctx.measureText(message);
        const textWidth = textMetrics.width;
        const boxWidth = textWidth + (boxPaddingX * 2);

        // Calculate slide-in position
        const finalX = 20;
        const startX = -boxWidth - 20;
        const boxX = startX + (finalX - startX) * easeOut;
        const boxY = 20;

        // Draw modern white box with shadow
        ctx.save();

        // Shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
        ctx.fillRect(boxX + 3, boxY + 3, boxWidth, boxHeight);

        // Main box
        ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, boxRadius);
        ctx.fill();

        // Border (orange for waiting)
        ctx.strokeStyle = '#ff6b35';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Text
        ctx.fillStyle = '#2c2c2c';
        ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(message, boxX + boxPaddingX, boxY + boxHeight / 2);

        ctx.restore();
      }
    }
  };

  const initializeHandDetection = async () => {
    if (!videoRef.current) {
      console.log('❌ No video ref available for hand detection');
      return;
    }

    console.log('🔧 Initializing new MediaPipe hands instance...');

    try {
      const hands = new Hands({
        locateFile: (file) => {
          const url = `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`;
          return url;
        }
      });

      await hands.setOptions({
        maxNumHands: 2,
        modelComplexity: 1,
        minDetectionConfidence: 0.7,
        minTrackingConfidence: 0.5
      });

      hands.onResults(onResults);

      handsRef.current = hands;
      console.log('✅ MediaPipe hands instance created and configured');

      // Wait a bit longer for MediaPipe to fully initialize
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Manual MediaPipe processing
      const processFrame = async () => {
        if (handsRef.current && videoRef.current && videoRef.current.readyState >= 2 && !confettiFiredRef.current) {
          try {
            await handsRef.current.send({ image: videoRef.current });
          } catch (error) {
            console.error('❌ Error sending frame to MediaPipe:', error);
          }
        }
        if (!confettiFiredRef.current) {
          requestAnimationFrame(processFrame);
        }
      };

      console.log('🚀 Starting MediaPipe frame processing...');
      processFrame();
    } catch (error) {
      console.error('❌ Failed to initialize MediaPipe:', error);
    }
  };

  // Set up video when both video element and camera stream are available
  useEffect(() => {
    if (cameraStream && videoRef.current && isPermissionGranted) {
      const video = videoRef.current;
      video.srcObject = cameraStream;

      video.oncanplay = async () => {
        startVideoDrawing();
        await initializeHandDetection();
      };

      video.play().catch((error) => {
        console.error('Video play failed:', error);
      });

      // Backup: start drawing after delay
      setTimeout(() => {
        startVideoDrawing();
      }, 1000);
    }
  }, [cameraStream, isPermissionGranted]);

  useEffect(() => {
    return () => {
      // Cleanup
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Check domain access first
  if (!isDomainAllowed) {
    return (
      <div className="app-container">
        <div className="permission-request">
          <h1>Access Restricted</h1>
          <p>This application can only be accessed from authorized domains.</p>
        </div>
      </div>
    );
  }

  if (isPermissionGranted === null) {
    if (isMobile) {
      return (
        <div className="app-container">
          <div className="permission-request">
            <h1>Jump Onto Your Computer To Practice With Your Webcam!</h1>
            <p>This experience works best using a webcam where you have both hands freely available. So load this experience up on your computer if you'd like to practice this way!</p>
          </div>
        </div>
      );
    }

    if (preselectedLetter) {
      return (
        <div className="app-container">
          <div className="permission-request">
            <h1>Try For Yourself!</h1>
            <p>Use your webcam to have a go at making the letter {preselectedLetter} using your hands on screen. Raise {preselectedLetter === 'C' ? 'one hand' : 'both your hands'} in front of you webcam until you see the tracking dots appear, and then make the correct sign.</p>
            <button onClick={requestCameraPermission} className="permission-button">
              Click Here To Start
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="app-container">
        <div className="permission-request">
          <h1>Try For Yourself!</h1>
          <p>Use your webcam to have a go at making the letter A using your hands on screen. Raise both your hands in front of you webcam until you see the tracking dots appear, and then make the correct sign.</p>
          <button onClick={() => { setSelectedLetter('A'); requestCameraPermission(); }} className="permission-button">
            Letter A
          </button>
          <button onClick={() => { setSelectedLetter('B'); requestCameraPermission(); }} className="permission-button" style={{marginLeft: '15px'}}>
            Letter B
          </button>
          <button onClick={() => { setSelectedLetter('C'); requestCameraPermission(); }} className="permission-button" style={{marginLeft: '15px'}}>
            Letter C
          </button>
        </div>
      </div>
    );
  }

  if (isPermissionGranted === false) {
    return (
      <div className="app-container">
        <div className="permission-denied">
          <p>Please enable camera access to use this application.</p>
          <button onClick={requestCameraPermission} className="permission-button">
            Click Here To Start
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="video-container">
        <video
          ref={videoRef}
          style={{
            display: 'none'
          }}
          width="800"
          height="600"
          autoPlay
          muted
          playsInline
        />
        <canvas
          ref={canvasRef}
          width="800"
          height="600"
          className="video-canvas"
          style={{ display: 'block' }}
        />
        {successDetected && (
          <div className="success-banner">
            🎉 You did it! This is the letter {selectedLetter} in BSL
          </div>
        )}
      </div>

      {showResetButton && (
        <button onClick={resetApplication} className="reset-button">
          Want To Go Again?
        </button>
      )}

      <Confetti show={showConfetti} />
    </div>
  );
}

export default App;